import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
    ConnectivityService,
    FedExRequest,
    LocationEntry,
    MaterialOrderFedexLocationModel,
    TextModel,
    TextModelOptions,
    TimeRange,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { BehaviorSubject, combineLatest, EMPTY, Observable, Subject, tap } from 'rxjs';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MaterialFedexLocationService } from '../services/material-fedex-location.service';
import { catchError, debounceTime, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { KeyValue } from '@angular/common';
import { compareWeekKeys, getWeeklySchedule, WEEK_ORDER } from './fedex-location-weekly-schedule.util';
import { faBan } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'pace-material-order-fedex-location-form',
    templateUrl: './material-order-fedex-location.component.html',
    styleUrls: ['./material-order-fedex-location.component.scss'],
    standalone: false,
})
export class MaterialOrderFedexLocationComponent implements OnInit, OnDestroy {
    @Input() public model: MaterialOrderFedexLocationModel;

    public fedexLocationForm: FormGroup;
    public formChildren: FormArray;
    public fedexLocations$: Observable<LocationEntry[]>;
    public readonly modalResult = new EventEmitter<LocationEntry>();
    public readonly isOnline$ = this.connectivityService.isOnline$;
    public hasLoadedFedexLocation = false;
    public hasFedexError = false;
    public isLoadingFedex = false;
    public readonly faBan = faBan;

    protected filterCriteriaModel: TextModel;
    protected readonly getWeeklySchedule = getWeeklySchedule;

    private locationsSubject = new BehaviorSubject<LocationEntry[]>([]);
    private filterSubject = new BehaviorSubject<string>('');
    private readonly destroySubject = new Subject<void>();
    private readonly loadFedexLocationsSubject = new Subject<void>();

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        public readonly materialFedexLocationService: MaterialFedexLocationService,
        public readonly modalInstance: BsModalRef,
        private readonly formBuilder: FormBuilder,
        private readonly connectivityService: ConnectivityService,
        private readonly cdr: ChangeDetectorRef,
    ) {
        this.filterCriteriaModel = new TextModel(
            'Filter',
            '',
            new TextModelOptions({ placeholder: 'Search FedEx Hold Location' }),
        );
        this.fedexLocations$ = combineLatest([
            this.locationsSubject.asObservable(),
            this.filterSubject.asObservable().pipe(debounceTime(300)),
        ]).pipe(
            map(([locations, filter]) => {
                if (!filter) {
                    return locations;
                }
                const lowFilter = filter.toLowerCase();
                return locations.filter(
                    (location) =>
                        location.Address.Description?.toLowerCase().includes(lowFilter) ||
                        location.Address.City?.toLowerCase().includes(lowFilter) ||
                        location.Address.Street?.toLowerCase().includes(lowFilter),
                );
            }),
            takeUntil(this.destroySubject),
        );
    }

    public ngOnInit(): void {
        this.formChildren = this.formBuilder.array([]);
        this.fedexLocationForm = this.formBuilder.group({
            formChildren: this.formChildren,
        });

        // Set up FedEx location loading pipeline
        this.loadFedexLocationsSubject
            .pipe(
                tap(() => {
                    this.isLoadingFedex = true;
                    this.hasFedexError = false;
                }),
                switchMap(() => {
                    const fedexLocationRequest = this.buildFedexPayload();
                    return this.materialFedexLocationService.getFedexLocations(fedexLocationRequest).pipe(
                        tap((locations: LocationEntry[]) => {
                            this.log.info(
                                `MaterialOrderFedexLocationComponent: FedEx: fetching locations succeeded, found: ${locations.length}`,
                            );
                            this.locationsSubject.next(locations);
                        }),
                        catchError((error) => {
                            this.log.error(
                                `MaterialOrderFedexLocationComponent: FedEx: fetching locations failed: ${JSON.stringify(error)}`,
                            );
                            this.hasFedexError = true;
                            return EMPTY;
                        }),
                        finalize(() => {
                            this.hasLoadedFedexLocation = true;
                            this.isLoadingFedex = false;
                        }),
                    );
                }),
                takeUntil(this.destroySubject),
            )
            .subscribe();
    }

    /**
     * Clears the filter criteria and resets the filter input field.
     *
     * Emits an empty string to the filter subject, which triggers the location list to display all results.
     * Also, manually triggers change detection to update the UI.
     */
    public clearFilter(): void {
        this.filterSubject.next('');
        this.filterCriteriaModel = {
            ...this.filterCriteriaModel,
            value: '',
        } as TextModel;

        this.cdr.detectChanges();
    }

    /**
     * Handles filter input changes and updates the location list in real-time.
     *
     * Extracts the input value, converts to lowercase, trims whitespace, and emits to the filter subject.
     * The filter subject is combined with the locations subject to provide filtered results.
     *
     * @param event The keyboard event from the filter input field
     */
    public onFilterCriteriaChange(event: KeyboardEvent): void {
        const value = (event.target as HTMLInputElement).value?.toLowerCase().trim() ?? '';
        this.filterSubject.next(value);
    }

    /**
     * Triggers a FedEx location search.
     *
     * Uses a subject-based pipeline with switchMap to prevent race conditions.
     * If a request is already in progress, this call is ignored.
     * If a new request is triggered while one is pending, the previous request is automatically cancelled.
     */
    public getFedexLocations(): void {
        this.loadFedexLocationsSubject.next();
    }

    /**
     * Handles FedEx location selection and closes the modal.
     *
     * Emits the selected location through the modalResult EventEmitter and hides the modal.
     * The parent component listens to this event to update the material order with the selected location.
     *
     * @param fedexLocation The FedEx location entry selected by the user
     */
    public selectFedexLocation(fedexLocation: LocationEntry): void {
        this.log.info(`MaterialOrderFedexLocationComponent: Location selected: ${fedexLocation.Address.Description}`);
        this.modalResult.emit(fedexLocation);
        this.modalInstance.hide();
    }

    private buildFedexPayload(): FedExRequest {
        return {
            Address: {
                City: this.model.city?.value ?? '',
                StateCode: this.model.statesOfUSA?.selectedEntry?.model?.value ?? '',
                Street: this.model.street?.value ?? '',
                PostalCode: this.model.zipCode?.value ?? '',
                CountryCode: this.model.country?.selectedEntry.model.value ?? 'US',
            },
            Weight: 0,
        };
    }

    public ngOnDestroy(): void {
        this.destroySubject.next();
        this.destroySubject.complete();
        this.modalResult.complete();
    }

    public close(): void {
        this.modalInstance.hide();
    }

    /**
     * Compares two weekly schedule keys for sorting purposes.
     *
     * Used by ngFor trackBy to maintain consistent ordering of weekday keys (MON, TUE, WED, etc.)
     * in the FedEx location details display.
     *
     * @param a First KeyValue pair with day name and time range
     * @param b Second KeyValue pair with day name and time range
     * @returns Comparison result for sorting (-1, 0, or 1)
     */
    public compareKeys(a: KeyValue<string, TimeRange>, b: KeyValue<string, TimeRange>): number {
        return compareWeekKeys(a, b, WEEK_ORDER);
    }
}
